import React, { useEffect, useMemo, useState } from "react";
import Spinner1 from "../common/spinners";
import moment from "moment";
import { FullscreenIcon, FlaskConical } from "lucide-react";
import { Button, Modal, Table, Form } from "react-bootstrap";
import { useLab } from "../../context/labContext";
import { formatCurrency } from "../../utils/utilities";

const LabExamsTab = () => {
  const [show, setShow] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const { handleFechLabRequestsAlt, requests, loading } = useLab();

  useEffect(() => {
    if (!requests?.length) {
      handleFechLabRequestsAlt();
    }
  }, []);

  const filteredData = useMemo(
    () =>
      (Array.isArray(requests) ? requests : []).filter(
        (item) =>
          item?.item?.labTest?.name?.toLowerCase()?.includes(searchTerm.toLowerCase()) ||
          item?.createdAt?.includes(searchTerm)
      ),
    [requests, searchTerm]
  );

  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const currentItems = filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const openModal = (lab) => {
    setSelectedLab(lab);
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setSelectedLab(null);
  };

  return (
    <div id="labs" className="w-100">
      <div className="data-panel-head">
        <div className="data-panel-copy">
          <h5>Lab examination results</h5>
          <p>Review investigations, specimen details, charges, and available results from one refined table.</p>
        </div>
        <div className="data-panel-actions">
          <div className="data-search">
            <Form.Control
              type="text"
              placeholder="Search lab test or date"
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="data-table-shell">
        {loading ? (
          <div className="w-100 d-flex justify-content-center py-5">
            <Spinner1 />
          </div>
        ) : currentItems.length === 0 ? (
          <div className="data-table-empty">
            <h6>No lab examination results found</h6>
            <p>Completed lab requests and available results will appear here.</p>
          </div>
        ) : (
          <div className="data-table-wrap">
            <table className="table data-table align-middle w-100">
              <thead>
                <tr>
                  <th>Lab Investigation</th>
                  <th>Specimen</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((lab) => (
                  <tr key={lab?.id}>
                    <td>
                      <div className="d-flex align-items-center gap-3">
                        <span className="data-icon-avatar">
                          <FlaskConical size={18} />
                        </span>
                        <div>
                          <div className="data-cell-title">{lab?.item?.labTest?.name}</div>
                          <div className="data-cell-meta">
                            Request: {moment(lab?.createdAt).format("dddd, Do MMMM, YYYY")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">
                        {lab?.item?.labTest?.specimens?.map((item) => item.label).join(", ")}
                      </div>
                      <div className="data-cell-meta">
                        {moment(lab?.item?.labTest?.createdAt).format("dddd, Do MMM YYYY")}
                      </div>
                    </td>
                    <td>
                      <div className="data-cell-title">{formatCurrency(lab?.item?.transaction?.amount)}</div>
                      <div className="data-cell-meta">
                        {moment(lab?.item?.transaction?.createdAt).format("dddd, Do MMM YYYY")}
                      </div>
                    </td>
                    <td>
                      <span className={`data-status-badge ${lab?.status === 0 ? "is-warning" : "is-success"}`}>
                        {lab?.status === 0 ? "Awaiting Payment" : "Completed"}
                      </span>
                    </td>
                    <td>
                      {lab?.status === 1 ? (
                        <button type="button" className="data-action-btn" onClick={() => openModal(lab)}>
                          <FullscreenIcon size={17} />
                        </button>
                      ) : (
                        <span className="data-cell-meta">Pending</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredData.length > 0 && (
          <div className="data-pagination">
            <Button variant="light" disabled={currentPage === 1} onClick={() => setCurrentPage((page) => page - 1)}>
              Previous
            </Button>
            <span>Page {currentPage} of {totalPages}</span>
            <Button
              variant="light"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((page) => page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>

      <Modal size="lg" show={show} onHide={handleClose} scrollable centered>
        <Modal.Header closeButton />
        <Modal.Body>
          {selectedLab ? (
            <div className="w-100">
              <ul style={{ listStyleType: "none", padding: 0 }}>
                <li className="mb-4">
                  Title: <span>{selectedLab?.item?.labTest?.name}</span>
                </li>
                <Table>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th>Value</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedLab?.item?.parameters?.map((x) => (
                      <tr key={x?.id}>
                        <td>{x?.name}</td>
                        <td>{x?.value}</td>
                        <td>{x?.reference}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </ul>
            </div>
          ) : (
            "No data available"
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LabExamsTab;
